import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaTimes, FaCheck, FaSortAmountDown, FaSortAmountUp, FaSpinner } from 'react-icons/fa';

const ColumnFilter = ({
  columnKey,
  columnLabel,
  uniqueValues = [],
  selectedValues = [],
  onFilterChange,
  onClear,
  onClose,
  onSort,
  currentSortKey,
  currentSortDirection,
  anchorEl,
  onFetchValues,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localValues, setLocalValues] = useState(uniqueValues);
  const [loading, setLoading] = useState(isLoading);
  // Track which values are selected
  const [localSelected, setLocalSelected] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  // Position the dropdown near the filter button
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const panelWidth = 260;
      const panelHeight = 380;
      
      let left = rect.left - 10;
      let top = rect.bottom + 2;
      
      // Adjust if going off right edge
      if (left + panelWidth > window.innerWidth) {
        left = window.innerWidth - panelWidth - 10;
      }
      
      // Adjust if going off left edge
      if (left < 10) {
        left = 10;
      }
      
      // Adjust if going off bottom edge
      if (top + panelHeight > window.innerHeight) {
        top = rect.top - panelHeight - 2;
      }
      
      setPosition({ top, left });
    }
  }, [anchorEl]);

  // Fetch values from server if onFetchValues is provided
  useEffect(() => {
    if (onFetchValues && typeof onFetchValues === 'function') {
      setLoading(true);
      onFetchValues(columnKey)
        .then((values) => {
          if (Array.isArray(values)) {
            setLocalValues(values);
            // Initialize selection based on existing filter or select all
            if (!initialized) {
              if (selectedValues.length > 0) {
                setLocalSelected(selectedValues);
              } else {
                setLocalSelected(values); // Select all by default
              }
              setInitialized(true);
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching filter values:', err);
          setLoading(false);
        });
    } else {
      setLocalValues(uniqueValues);
      if (!initialized) {
        if (selectedValues.length > 0) {
          setLocalSelected(selectedValues);
        } else {
          setLocalSelected(uniqueValues); // Select all by default
        }
        setInitialized(true);
      }
    }
  }, [columnKey, onFetchValues, uniqueValues, initialized]);

  useEffect(() => {
    // Focus search input when opened
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Filter values based on search term
  const filteredValues = useMemo(() => {
    if (!searchTerm) return localValues;
    return localValues.filter(value =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [localValues, searchTerm]);

  // Check if all values are selected
  const isAllSelected = localValues.length > 0 && localSelected.length === localValues.length;
  
  // Check if some but not all are selected
  const isIndeterminate = localSelected.length > 0 && localSelected.length < localValues.length;

  const handleToggleValue = (value) => {
    setLocalSelected(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setLocalSelected([...localValues]);
    } else {
      setLocalSelected([]);
    }
  };

  const handleApply = () => {
    // If nothing selected, don't apply (would show no results)
    if (localSelected.length === 0) {
      onClear();
      onClose();
      return;
    }
    
    // If search is active, only consider the filtered (visible) values
    if (searchTerm) {
      const visibleSelected = localSelected.filter(v => filteredValues.includes(v));
      if (visibleSelected.length === 0) {
        onClear();
        onClose();
        return;
      }
      // Apply filter with only the visible selected values
      onFilterChange(visibleSelected);
      onClose();
      return;
    }
    
    // If all are selected, clear the filter (no filter active = show all)
    if (localSelected.length === localValues.length) {
      onClear();
    } else {
      // Apply filter with selected values
      onFilterChange(localSelected);
    }
    onClose();
  };

  const handleClearFilter = () => {
    onClear();
    onClose();
  };

  const handleSortAsc = () => {
    if (onSort) {
      onSort(columnKey, 'asc');
    }
    onClose();
  };

  const handleSortDesc = () => {
    if (onSort) {
      onSort(columnKey, 'desc');
    }
    onClose();
  };

  const isCurrentSortAsc = currentSortKey === columnKey && currentSortDirection === 'asc';
  const isCurrentSortDesc = currentSortKey === columnKey && currentSortDirection === 'desc';

  // Check if filter is currently active (has saved selection that's not all)
  const isFilterActive = selectedValues.length > 0 && selectedValues.length < localValues.length;

  return (
    <div className="excel-filter-overlay">
      <div 
        className="excel-filter-dropdown"
        ref={panelRef}
        style={{ top: position.top, left: position.left }}
      >
        {/* Sort Options - Like Excel */}
        <div className="excel-filter-sort-section">
          <button 
            className={`excel-filter-sort-btn ${isCurrentSortAsc ? 'active' : ''}`}
            onClick={handleSortAsc}
          >
            <FaSortAmountUp />
            <span>Sort A to Z</span>
          </button>
          <button 
            className={`excel-filter-sort-btn ${isCurrentSortDesc ? 'active' : ''}`}
            onClick={handleSortDesc}
          >
            <FaSortAmountDown />
            <span>Sort Z to A</span>
          </button>
        </div>

        <div className="excel-filter-divider" />

        {/* Search Box */}
        <div className="excel-filter-search">
          <FaSearch className="excel-filter-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="excel-filter-search-input"
          />
          {searchTerm && (
            <button 
              className="excel-filter-clear-search"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="excel-filter-loading">
            <FaSpinner className="excel-filter-spinner" />
            <span>Loading values...</span>
          </div>
        ) : (
          <>
            {/* Select All Checkbox */}
            <div className="excel-filter-select-all">
              <label className="excel-filter-checkbox-label">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  ref={input => {
                    if (input) {
                      input.indeterminate = isIndeterminate;
                    }
                  }}
                />
                <span className="excel-filter-checkbox-box">
                  {isAllSelected && <FaCheck />}
                  {isIndeterminate && <span style={{ fontSize: '8px' }}>—</span>}
                </span>
                <span className="excel-filter-checkbox-text">(Select All)</span>
              </label>
            </div>

            {/* Values List */}
            <div className="excel-filter-values">
              {filteredValues.length === 0 ? (
                <div className="excel-filter-no-results">No results found</div>
              ) : (
                filteredValues.map((value, index) => (
                  <label key={index} className="excel-filter-checkbox-label">
                    <input
                      type="checkbox"
                      checked={localSelected.includes(value)}
                      onChange={() => handleToggleValue(value)}
                    />
                    <span className="excel-filter-checkbox-box">
                      {localSelected.includes(value) && <FaCheck />}
                    </span>
                    <span className="excel-filter-checkbox-text" title={value}>
                      {value}
                    </span>
                  </label>
                ))
              )}
            </div>
          </>
        )}

        {/* Footer Buttons */}
        <div className="excel-filter-footer">
          <button 
            className="excel-filter-btn excel-filter-btn-ok"
            onClick={handleApply}
            disabled={loading}
          >
            OK
          </button>
          <button 
            className="excel-filter-btn excel-filter-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          {isFilterActive && (
            <button 
              className="excel-filter-btn excel-filter-btn-clear"
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColumnFilter;
